/**
 * Own profile: edit personal data (PATCH /users/:id, multipart) and
 * change the password (PATCH /users/changePassword — the regular user
 * update endpoint refuses password changes).
 */

import { useEffect, useState } from 'react';
import { useGetIdentity, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { httpClient } from '@/utils/httpClient';
import { getCurrentUser, storeAuthData, getToken } from '@/utils/auth';
import type { ApiEnvelope, ApiUser } from '@/types/api';

export const ProfilePage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [profile, setProfile] = useState({ full_name: '', contact_no: '', city: '', address: '' });
  const [loaded, setLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', next: '', confirm: '' });
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const user = getCurrentUser();
    if (user) {
      setProfile({
        full_name: user.full_name ?? '',
        contact_no: user.contact_no ?? '',
        city: user.city ?? '',
        address: user.address ?? '',
      });
    }
    setLoaded(true);
  }, []);

  const saveProfile = async () => {
    if (!identity?.id) return;
    setSavingProfile(true);
    try {
      const form = new FormData();
      Object.entries(profile).forEach(([key, value]) => form.append(key, value ?? ''));
      const response = await httpClient.patch<ApiEnvelope<ApiUser>>(
        `/users/${identity.id}`,
        form
      );
      if (response.data.status === 'error') {
        notify(response.data.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        // keep the stored identity in sync so the app bar shows the new name
        const user = getCurrentUser();
        const token = getToken();
        if (user && token) {
          storeAuthData(token, { ...user, ...profile });
        }
        notify('pages.profile.saved', { type: 'info' });
      }
    } catch (error: any) {
      notify(error?.message || 'ra.notification.http_error', { type: 'error' });
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (passwords.next !== passwords.confirm) {
      notify('pages.profile.password_mismatch', { type: 'warning' });
      return;
    }
    setSavingPassword(true);
    try {
      const response = await httpClient.post<ApiEnvelope>('/users/changePassword', {
        currentPassword: passwords.current,
        newPassword: passwords.next,
      });
      if (response.data.status === 'error') {
        notify(response.data.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        notify('pages.profile.password_changed', { type: 'info' });
        setPasswords({ current: '', next: '', confirm: '' });
      }
    } catch (error: any) {
      notify(error?.message || 'ra.notification.http_error', { type: 'error' });
    } finally {
      setSavingPassword(false);
    }
  };

  if (!loaded) return <CircularProgress size={28} sx={{ m: 3 }} />;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('pages.profile.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {translate('pages.profile.data')}
              </Typography>
              <TextField
                label={translate('resources.staff.fields.full_name')}
                value={profile.full_name}
                onChange={(e) => setProfile((p) => ({ ...p, full_name: e.target.value }))}
              />
              <TextField label={translate('resources.staff.fields.email')} value={identity?.email ?? ''} disabled />
              <TextField
                label={translate('resources.staff.fields.contact_no')}
                value={profile.contact_no}
                onChange={(e) => setProfile((p) => ({ ...p, contact_no: e.target.value }))}
              />
              <TextField
                label={translate('resources.organizations.fields.city')}
                value={profile.city}
                onChange={(e) => setProfile((p) => ({ ...p, city: e.target.value }))}
              />
              <TextField
                label={translate('resources.organizations.fields.address')}
                value={profile.address}
                onChange={(e) => setProfile((p) => ({ ...p, address: e.target.value }))}
              />
              <Button variant="contained" onClick={saveProfile} disabled={savingProfile} sx={{ width: 'fit-content' }}>
                {translate('ra.action.save')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {translate('pages.profile.change_password')}
              </Typography>
              <TextField
                type="password"
                autoComplete="current-password"
                label={translate('pages.profile.current_password')}
                value={passwords.current}
                onChange={(e) => setPasswords((p) => ({ ...p, current: e.target.value }))}
              />
              <TextField
                type="password"
                autoComplete="new-password"
                label={translate('pages.profile.new_password')}
                value={passwords.next}
                onChange={(e) => setPasswords((p) => ({ ...p, next: e.target.value }))}
              />
              <TextField
                type="password"
                autoComplete="new-password"
                label={translate('pages.profile.confirm_password')}
                value={passwords.confirm}
                onChange={(e) => setPasswords((p) => ({ ...p, confirm: e.target.value }))}
              />
              <Button
                variant="contained"
                onClick={changePassword}
                disabled={savingPassword || !passwords.current || !passwords.next}
                sx={{ width: 'fit-content' }}
              >
                {translate('pages.profile.change_password')}
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProfilePage;
